/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';

import { Tooltip, CopyText, Modal } from '../../../../UiKit';
import { StarFill } from 'sushiweb/Icons';
import { ValetTags } from '../ValetTags';
import { WFHCall } from '../../../commons';
import { valetModel } from '../../../../api';
import toast from 'react-hot-toast';
import {
  VALET_EVENTS,
  sendValetApiEvents,
} from '../../../../helpers/analytics/valet.analytics';

import { nonLogsRider, smallRider } from '../../../../images/images';
import { ValetInstructionsV1 } from '../../Order';
import { OrderOtp } from '../OrderOtp';
import { lifelineEndPoints } from '../../../../api/end-points';
import { useQueryCall } from '../../../../api/react-client';
import { Bike, Eye, Headset, EarOff, LoaderCircle } from 'lucide-react';
import { ValetCallClickEvent } from '../../../../analytics/call-logging/events';
import { RiderAssignmentsV1 } from '../../../popups/RiderAssignments/RiderAssignmentsV1';
import { Accessibility } from 'lucide-react';
import { WalkerDetails } from '../WalkerDetails';

const ambulanceRoles = [
  'Paramedic',
  'Ambulance Pilot',
  'General Duty Assistant',
];

const NoValetAssigned = ({
  orderStatus,
  assignmentsHistoryComp,
  walkerDetailsComp,
}) => {
  return (
    <div className='noValetAssignedContainer tw-h-full tw-items-center tw-justify-center'>
      <div className='noValetAssignedImgContainer tw-items-center tw-justify-center tw-mb-2 tw-gap-2'>
        <div className='noValetAssignedImg'>
          <img src={smallRider} alt='small-rider' />
        </div>
      </div>

      {orderStatus === 'DELIVERED' ? (
        <div className='tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2'>
          <div className='tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-mb-4'>
            <p className='tw-text-400 tw-font-medium tw-text-grey-800 tw-leading-none'>
              Valet details unavailable
            </p>
            <p className='lighter tw-text-300 tw-font-medium tw-text-black tw-leading-none'>
              Order was delivered
            </p>
          </div>
          {assignmentsHistoryComp}
          {walkerDetailsComp}
        </div>
      ) : (
        <div className='tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2'>
          <div className='tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-mb-4'>
            <p className='tw-text-400 tw-font-medium tw-text-grey-800 tw-leading-none'>
              No Valet assigned
            </p>
            {orderStatus === 'CANCELLED' ? (
              <p className='lighter tw-text-300 tw-font-medium tw-text-black tw-leading-none'>
                Order was cancelled before Valet assignment
              </p>
            ) : null}
          </div>
          {assignmentsHistoryComp}
          {walkerDetailsComp}
        </div>
      )}
    </div>
  );
};

function unmaskNumber(orderID, setNumber, setisMask) {
  const queryParams = {
    order_id: orderID,
  };

  valetModel
    .getValetNumber(queryParams)
    .then((response) => {
      if (!response || response?.isError === true) {
        return toast.error(
          response.message || 'Something went wrong. Please try again later!',
        );
      }

      sendValetApiEvents(VALET_EVENTS.GET_VALET_NUMBER_SUCCESS, orderID);
      setisMask(false);
      setNumber(response.phone_number);
    })
    .catch((err) => {
      toast.error(
        err.message || 'Something went wrong. Please try again later!',
      );

      sendValetApiEvents(VALET_EVENTS.GET_VALET_NUMBER_FAILURE, orderID);
    });
}

const ValetPhoneNumbers = ({
  phoneNumbers,
  isWFH,
  orderId,
  userId,
  cartId = null,
  valetName = '',
  calledTo = 'Rider',
  isCallingEnabled = true,
  callingDisabledReason = '',
}) => {
  const [isMask, setisMask] = useState(true);
  if (!phoneNumbers || (phoneNumbers && !phoneNumbers.length)) return null;
  const valetNumbers = phoneNumbers.slice(0, 2);
  return (
    <div
      className='tw-flex tw-flex-col tw-gap-x-3 tw-w-full'
      data-dd-privacy='mask'
    >
      {valetNumbers.map((number, index) => {
        const [phone, setPhone] = useState(number);
        return (
          <div key={index} className='tw-flex tw-items-center tw-gap-3'>
            <div className='tw-flex tw-items-center tw-text-zinc-600 tw-font-regular'>
              <Headset
                size={16}
                className='tw-text-grey-600 tw-mr-2'
                strokeWidth={2}
              />

              {isMask ? (
                <CopyText
                  dontShowCopyIcon={true}
                  title={phone}
                  orderId={orderId}
                  userId={userId}
                  masking={false}
                  maskingCount={4}
                  calledTo={calledTo}
                />
              ) : (
                <div>
                  <CopyText
                    title={phone}
                    orderId={orderId}
                    userId={userId}
                    masking={false}
                    maskingCount={4}
                    calledTo={calledTo}
                  />
                </div>
              )}
              {isMask && (
                <Eye
                  size={16}
                  strokeWidth={1.5}
                  className='tw-cursor-pointer tw-text-grey-400 hover:tw-text-grey-500 tw-transition-colors tw-ease-in-out tw-duration-200'
                  onClick={() => unmaskNumber(orderId, setPhone, setisMask)}
                  data-dd-action-name='Unmask Rider Number'
                />
              )}
            </div>
            {isWFH ? (
              <WFHCall
                isCallingDisabled={!isCallingEnabled}
                callingDisabledReason={callingDisabledReason}
                orderId={orderId}
                userId={userId}
                title={number}
                calledTo={calledTo}
                sendJumboEvent={true}
                jumboEventFunc={ValetCallClickEvent}
                JumboEventProperties={{
                  valet_name: valetName,
                  valet_number: number,
                  is_new_ui: true,
                }}
                cartId={cartId}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const AccessibilityBanner = ({ icon = null, title, description }) => {
  return (
    <div className='tw-flex tw-items-start tw-gap-3 tw-bg-red-100 tw-rounded-lg tw-px-3 tw-py-2 tw-mt-1'>
      {icon && <div className='tw-mt-[2px]'>{icon}</div>}
      <div className='tw-flex tw-flex-col tw-gap-[2px]'>
        <span className='tw-text-zinc-800 tw-font-medium tw-text-[1.3rem] tw-leading-tight'>
          {title}
        </span>
        <span className='tw-text-zinc-600 tw-font-regular tw-text-[1.3rem] tw-leading-tight'>
          {description}
        </span>
      </div>
    </div>
  );
};

export const ValetInfoV1 = ({
  valetTitle = '',
  orderId,
  valet = {},
  valetId,
  userId,
  isWFH,
  noData,
  orderBasicInfo,
  getOrderBasicInfo,
}) => {
  const num_of_buddy_riders = valet?.buddy_riders?.length;

  const isDisabledValet = valet?.tags?.some(
    (tag) =>
      tag?.text === 'Physically Disabled Rider' ||
      tag?.text === 'Hearing/Speech Impaired Rider',
  );

  const isHearingSpeechImpaired = valet?.tags?.some(
    (tag) => tag?.text === 'Hearing/Speech Impaired Rider',
  );

  const isPhysicallyDisabled = valet?.tags?.some(
    (tag) => tag?.text === 'Physically Disabled Rider',
  );

  const assignmentsHistoryComp = (
    <AssignmentHistory
      orderId={orderId}
      userId={userId}
      cartId={orderBasicInfo?.order?.cart_id}
      showBuddyRiderLabel={num_of_buddy_riders}
      numberOfBuddyRider={num_of_buddy_riders}
    />
  );

  const walkerDetailsComp = (
    <WalkerDetails
      walker={valet?.walker_details}
      orderId={orderId}
      userId={userId}
      cartId={orderBasicInfo?.order?.cart_id}
    />
  );

  if (noData) {
    return (
      <div className='tw-flex tw-relative tw-flex-col tw-border-solid tw-border-[1px] tw-rounded-[8px] tw-border-grey-200 tw-h-full tw-w-full'>
        <div className='noValetAssignedContainer tw-h-full tw-items-center tw-justify-center'>
          <div className='noValetAssignedImgContainer tw-items-center tw-justify-center tw-mb-2 tw-gap-2'>
            <div className='noValetAssignedImg'>
              <img src={nonLogsRider} alt='small-rider' />
            </div>
          </div>

          <div className='tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2'>
            <div className='tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-mb-4'>
              <p className='tw-text-400 tw-font-medium tw-text-grey-800 tw-leading-none'>
                {valetTitle}
              </p>
            </div>
            {assignmentsHistoryComp}
          </div>
        </div>
      </div>
    );
  }

  const valetName = valet?.name ? valet.name : '';
  const valetText = valet?.tags?.length > 0 ? valet.tags[0].text : '';
  const calledTo = ambulanceRoles
    .map((role) => role.toLowerCase())
    .includes(valetText.toLowerCase())
    ? valetText
    : 'Rider';

  if (!valetName) {
    return (
      <div className='valetInfoContainer tw-h-full tw-w-full tw-py-3 tw-pl-3 tw-pr-5 '>
        <NoValetAssigned
          orderStatus={valet?.order_status}
          assignmentsHistoryComp={assignmentsHistoryComp}
          walkerDetailsComp={walkerDetailsComp}
        />
      </div>
    );
  }

  const valetPhoneNumbers = valet?.phone_number
    ? valet?.phone_number.split(',')
    : '';

  const showValetInstruction = orderBasicInfo?.order?.is_live
    ? true
    : orderBasicInfo?.order?.delivery_instruction.length
    ? true
    : false;

  return (
    <div className='tw-flex tw-relative tw-flex-col tw-border-solid tw-border-[1px] tw-rounded-[8px] tw-border-grey-200 tw-h-full tw-w-full'>
      {/* Upper section */}
      <div className='tw-border-solid tw-h-[65px] tw-w-full tw-border-[1px] tw-border-grey-200 tw-border-x-0 tw-border-t-0 tw-p-2 tw-flex tw-items-center tw-gap-2'>
        {/* Valet icon and rating */}
        <div className='tw-h-full tw-aspect-square tw-bg-teal-100 tw-flex-col tw-rounded-[6px] tw-justify-center tw-items-center tw-border-solid tw-border-teal-200/50 tw-border-[1px]'>
          <div className='tw-h-full tw-flex tw-justify-center tw-items-center'>
            {!isDisabledValet ? (
              <Bike size={22} strokeWidth={1.7} />
            ) : (
              <Accessibility size={26} strokeWidth={1.7} />
            )}
          </div>
          {valet?.rating?.text && (
            <div
              style={{
                backgroundColor: valet?.rating?.bg_color || '#0a757c',
                color: valet?.rating?.color || 'white',
              }}
              className='tw-rounded-b-[6px] tw-text-[1.3rem] tw-flex tw-items-center tw-justify-center tw-w-full tw-gap-1 tw-font-medium'
            >
              {valet?.rating?.text}
              <StarFill size={10} color={valet?.rating?.color || 'white'} />
            </div>
          )}
        </div>

        <div className='tw-flex-col tw-h-full tw-justify-between tw-w-full'>
          <div className='tw-flex tw-justify-between tw-w-full tw-gap-2 tw-items-center'>
            <div className='tw-text-zinc-500 tw-font-medium tw-flex tw-gap-1'>
              <span className='tw-whitespace-nowrap'>Rider #</span>
              <CopyText title={valetId} />
            </div>
            {valet?.tags?.length ? <ValetTags valet={valet} /> : null}
          </div>

          <div
            className='tw-flex tw-justify-between tw-items-end tw-gap-2'
            data-dd-privacy='mask'
          >
            <Tooltip
              title={
                <span
                  className={`tw-text-[16px] tw-mb-[1px] tw-text-zinc-600 tw-font-medium tw-whitespace-nowrap tw-overflow-hidden 
                    tw-text-center tw-text-ellipsis tw-max-w-[280px]`}
                  data-dd-privacy='mask'
                >
                  {valetName}
                </span>
              }
              position={'bottom'}
              border={false}
              label={valetName}
              maskData={true}
            />
          </div>
        </div>
      </div>

      {/* Middle section */}
      <div className='tw-border-solid tw-min-h-[95px] tw-p-3 tw-gap-2 tw-border-[1px] tw-border-[#e8e8e8] tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-flex-col tw-bg-grey-100/40 tw-justify-between'>
        <div className='tw-flex-col tw-gap-3 tw-w-full tw-justify-between tw-h-full'>
          <div className='tw-flex-col tw-gap-2' data-dd-privacy='mask'>
            {valetPhoneNumbers.length > 0 && (
              <ValetPhoneNumbers
                phoneNumbers={valetPhoneNumbers}
                isWFH={isWFH}
                orderId={orderId}
                userId={userId}
                cartId={orderBasicInfo?.order?.cart_id}
                calledTo={calledTo}
                valetName={valetName}
                isCallingEnabled={valet?.calling_enabled}
                callingDisabledReason={valet?.calling_disabled_reason}
              />
            )}
            {isHearingSpeechImpaired && (
              <AccessibilityBanner
                icon={
                  <EarOff
                    size={20}
                    strokeWidth={1.7}
                    className='tw-text-zinc-800 tw-flex-shrink-0'
                  />
                }
                title='Hearing/Speech Impaired'
                description='This rider may not be able to communicate over calls. Avoid calling unless necessary.'
              />
            )}
            {isPhysicallyDisabled && (
              <AccessibilityBanner
                title='Physically Disabled rider'
                description='Rider may struggle with stairs/long walks. Politely request customer cooperation.'
              />
            )}
          </div>

          <div className='tw-flex tw-justify-between tw-items-end tw-gap-2 tw-leading-none tw-mt-[1px]'>
            {orderBasicInfo?.order?.is_otp_enabled && (
              <OrderOtp orderId={orderId} />
            )}
            {orderBasicInfo?.order?.is_contactless &&
              orderBasicInfo.order.contactless_image && (
                <a
                  href={orderBasicInfo.order.contactless_image}
                  className='tw-text-300 tw-font-medium tw-text-base-green tw-cursor-pointer tw-leading-none'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Contactless Image
                </a>
              )}
          </div>
        </div>
      </div>

      {/* Lower section */}
      <div className='tw-px-3 tw-py-4 tw-pr-5 tw-w-full tw-flex-1 tw-flex tw-flex-col tw-justify-end tw-gap-3'>
        <div className='tw-flex tw-flex-col tw-justify-between tw-gap-4 tw-leading-none tw-w-full'>
          {showValetInstruction && (
            <ValetInstructionsV1
              order={orderBasicInfo}
              orderId={orderId}
              userId={userId}
              retry={getOrderBasicInfo}
            />
          )}
          {assignmentsHistoryComp}
          {walkerDetailsComp}
        </div>
      </div>
    </div>
  );
};

const AssignmentHistory = ({
  orderId,
  showBuddyRiderLabel = false,
  numberOfBuddyRider = null,
  userId,
  cartId,
}) => {
  const [showDPHistory, setShowDPHistory] = useState(false);
  const getAssignmentsHistoryEndPoint = `${lifelineEndPoints.GET_ASSIGNMENT_HISTORY}?order_id=${orderId}`;

  const { isLoading, data, isError } = useQueryCall(
    {
      enabled: showDPHistory,
    },
    getAssignmentsHistoryEndPoint,
    ['lifeline_apis', 'logistics', 'get_assignment_history', orderId],
  );

  const handleShowDPHistory = () => {
    setShowDPHistory(true);
  };

  return (
    <>
      <div
        className='tw-text-300 tw-font-medium tw-text-base-green tw-cursor-pointer tw-leading-none'
        onClick={handleShowDPHistory}
      >
        View Assignment History
        {showBuddyRiderLabel
          ? ` (+${numberOfBuddyRider ?? 0} Buddy ${
              numberOfBuddyRider == 1 ? 'rider' : 'riders'
            })`
          : ''}
      </div>
      <Modal
        isVisible={showDPHistory}
        padding={0}
        paddingTop={'1rem'}
        type='huge'
        height='100%'
        maxHeight='80vh'
        containerStyles={{ margin: '12px 0 20px 0', padding: '0 20x' }}
        titleComponent={
          <span className='tw-text-[2rem] tw-font-medium tw-text-zinc-600'>
            Delivery Partner Assignments History
          </span>
        }
        handleClose={() => setShowDPHistory(false)}
      >
        <div className='tw-overflow-hidden'>
          {isLoading && (
            <div className='tw-flex tw-items-center tw-justify-center tw-w-full tw-h-[10rem]'>
              <LoaderCircle className='tw-animate-spin tw-text-zinc-500' />
            </div>
          )}
          {!isError && !isLoading && (
            <RiderAssignmentsV1
              data={data}
              orderId={orderId}
              userId={userId}
              cartId={cartId}
            />
          )}
        </div>
      </Modal>
    </>
  );
};
